import React, { useEffect, useState } from "react";
import "./Dialer.css";
import Dialpad, {
  extractPhoneDigits,
  formatPhoneDigits,
  toDialablePhoneNumber,
} from "./Dialpad";
import ActiveCallCard from "./ActiveCallCard";
import NotesPanel from "./NotesPanel";
import SaveContactScreen from "./SaveContactScreen";
import RecentCalls from "./RecentCalls";
import { useCallEngine } from "./useCallEngine";

export default function DialerPage() {
  const [callPhase, setCallPhase] = useState("ready");
  const [dialValue, setDialValue] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [contactName, setContactName] = useState("");
  const displayDialValue = dialValue ? formatPhoneDigits(dialValue) : "";
  const dialValueForEngine = toDialablePhoneNumber(dialValue);
  const dialableDigitsCount = extractPhoneDigits(dialValue).length;

  const callEngine = useCallEngine({ dialValue: dialValueForEngine });
  const isCompactReadyState = callPhase === "ready";

  useEffect(() => {
    setCallPhase(callEngine.callPhase);
  }, [callEngine.callPhase]);

  useEffect(() => {
    if (callPhase === "ready" && !dialValue) {
      setContactName("");
    }
  }, [callPhase, dialValue]);

  const handleStartCall = async () => {
    await callEngine.startCall();
  };

  const handleEndCall = async () => {
    await callEngine.endCall();
  };

  const handleReturnToReady = () => {
    callEngine.resetDialer();
    setCallPhase("ready");
    setNoteDraft(callEngine.defaultNotes);
    setContactName("");
  };

  const handleSelectRecentCall = (entry) => {
    if (
      callEngine.callState === "connecting" ||
      callEngine.callState === "in_call"
    ) {
      return;
    }

    callEngine.resetDialer();
    setDialValue(extractPhoneDigits(entry.number || ""));
    setNoteDraft(callEngine.defaultNotes);
    setContactName(entry.savedName || "");
    setCallPhase("ready");
  };

  const handleSaveNotes = async () => {
    await callEngine.saveNotes(noteDraft);
  };

  const handleSaveContact = async () => {
    await callEngine.saveContact({
      number: dialValueForEngine,
      name: contactName,
    });
  };

  const renderLeftWorkspace = () => {
    if (callPhase === "ready") {
      return (
        <Dialpad
          value={dialValue}
          outboundNumber={callEngine.outboundNumber}
          onChange={(value) => {
            setDialValue(value);
          }}
          onAppendDigit={(digit) =>
            setDialValue((currentValue) =>
              extractPhoneDigits(`${currentValue}${digit}`),
            )
          }
          onBackspace={() =>
            setDialValue((currentValue) => currentValue.slice(0, -1))
          }
          onClear={() => setDialValue("")}
          onStartCall={handleStartCall}
          isBusy={callEngine.callState === "connecting"}
          isStartDisabled={
            dialableDigitsCount !== 10 ||
            !callEngine.selectedAgent ||
            callEngine.callState !== "idle" ||
            callEngine.isCallLockedByOtherTab
          }
          callError={
            callEngine.isCallLockedByOtherTab
              ? "Call already in progress in another tab"
              : callEngine.callError
          }
          startButtonLabel={
            callEngine.callState === "connecting" ? "Connecting" : "Call"
          }
        />
      );
    }

    if (callPhase === "active") {
      return (
        <div className="dialer-active-workspace">
          <div className="dialer-active-card-slot">
            <ActiveCallCard
              dialValue={displayDialValue}
              outboundNumber={callEngine.outboundNumber}
              duration={callEngine.duration}
              isConnected={callEngine.isConnected}
              isMuted={callEngine.isMuted}
              onMute={callEngine.toggleMute}
              onEndCall={handleEndCall}
            />
          </div>

          <div className="dialer-active-content-slot">
            <div className="dialer-split-workspace dialer-notes-workspace workspace-panel-slide">
              <div className="dialer-split-panel">
                <NotesPanel
                  notes={noteDraft}
                  onChange={setNoteDraft}
                  onSave={handleSaveNotes}
                  isSaving={callEngine.isSavingNotes}
                  feedback={callEngine.notesFeedback}
                  isSaveDisabled={!noteDraft.trim()}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <SaveContactScreen
        dialValue={displayDialValue}
        duration={callEngine.duration}
        notes={noteDraft}
        contactName={contactName}
        onContactNameChange={setContactName}
        onSaveContact={handleSaveContact}
        isSavingContact={callEngine.isSavingContact}
        contactFeedback={callEngine.contactFeedback}
        onReturnToReady={handleReturnToReady}
      />
    );
  };

  return (
    <div className={`dialer-page-shell dialer-phase-${callPhase}`}>
      <header className="dialer-page-header">
        <div className="dialer-page-title-row">
          <h1 className="dialer-page-title">Outbound Dialer</h1>
          <div className="dialer-status-group">
            <span className="dialer-status-pill">
              Phase <strong>{callPhase}</strong>
            </span>
          </div>
        </div>
      </header>

      {!callEngine.loadingAgents && callEngine.agents.length === 0 ? (
        <div className="dialer-inline-feedback is-warning">
          No outbound-capable lines are configured for this practice yet.
        </div>
      ) : null}

      <div
        className={`dialer-layout${
          isCompactReadyState ? " is-ready" : " is-expanded"
        }`}
      >
        <section
          className={`dialer-left-workspace main-content left-pane${
            isCompactReadyState ? " is-compact" : " is-expanded"
          }`}
        >
          <div className="dialer-left-surface dialer-shell-surface">
            <div className="dialer-left-content">{renderLeftWorkspace()}</div>
          </div>
        </section>

        <aside className="dialer-right-sidebar sidebar right-sidebar">
          <div className="dialer-right-surface">
            <div className="dialer-right-content">
              <RecentCalls
                items={callEngine.recentCalls}
                isLoading={callEngine.loadingRecentCalls}
                onSelectCall={handleSelectRecentCall}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

